import type { FramedProduct } from '@rpl/pricing';
import { useEffect, useMemo, useState } from 'react';
import roomImg from '../../../assets/room-livingroom.jpg';
import type { Rotation } from '../../../lib/rotate';
import type { BwStyle, CropRect } from '../../../lib/types';
import { Frame3DView } from './Frame3DView';
import { FramePreview } from './FramePreview';

/**
 * Three-way framed-preview switcher (owner spec 2026-07-25): Normal (the flat
 * CSS mock), 3D (default — Frame3DView: self-rotating sweep + drag-to-rotate
 * + glass glare) and In room (the frame composited at true relative scale on
 * a straight-on living-room wall). Used by the Size & Paper configure preview
 * and the cart's Add-a-frame dialog; the Adjustments editor renders
 * Frame3DView directly (3D only, no tabs — owner 2026-07-26).
 *
 * Layout (owner round 3): the tab row is pinned at the top of the host stage
 * and the active mode renders in the .fpvm__view area below it, so switching
 * modes never moves the tabs (see cart.css .fpvm for the height budget).
 * Switching modes REMOUNTS the 3D view, restarting its sway fresh.
 */

/* Room-scene calibration. The shot is straight-on, so no perspective warp is
   needed — only scale + placement. All values are FRACTIONS of the image so
   any render size stays correct.
   - The 2-seater sofa measures ~37.5% of the image width and is ~1.2 m long
     (owner-corrected estimate) -> fraction-per-metre = 0.375 / 1.2.
   - The frame hangs from a fixed BOTTOM line (owner round 3): its bottom edge
     sits at bottomY, a clear gap above the sofa back (measured at y ≈ 0.645
     of the 1600×1240 asset), so the frame can never overlap the sofa. Zooming
     scales the scene around (anchorX, bottomY), which only moves the sofa
     further DOWN — the gap grows, never shrinks.
   - Small frames ZOOM the scene in around the anchor (owner round 2) so a 6x8
     never reads as a postage stamp: zoom targets the frame at ~minWFrac of the
     stage, capped at maxZoom. maxWFrac clamps oversized frames so they never
     crowd the pendant lamp; tall frames are additionally clamped so their TOP
     stays topMargin below the tab bar (height fraction ≤ bottomY − topMargin). */
const ROOM = {
  fracPerMetre: 0.3125,
  anchorX: 0.47,
  /** The frame's BOTTOM edge as a fraction of image height (sofa back ≈ 0.645). */
  bottomY: 0.6,
  maxWFrac: 0.34,
  minWFrac: 0.16,
  maxZoom: 2.2,
  /** Minimum headroom above the frame's top edge (fraction of image height). */
  topMargin: 0.06,
  /** The asset's w/h (1600×1240) — converts width fractions to height fractions. */
  imgAspect: 1600 / 1240,
};

type Mode = 'flat' | 'threed' | 'room';

const MODES: Array<[Mode, string]> = [
  ['flat', 'Normal'],
  ['threed', '3D'],
  ['room', 'In room'],
];

function parseInches(s: string): [number, number] {
  const [a, b] = s.split('x').map(Number);
  return [a || 1, b || 1];
}

export function FramePreviewModes({
  photo,
  option,
  cropRect,
  rotation,
  bw,
  bwStyle = null,
  bwStrength = null,
  fit,
}: {
  photo: { previewUrl: string | null; width: number; height: number } | undefined;
  option: FramedProduct;
  /** Optional LIVE render props (staged pre-Add adjustments) — passed through
   *  to every mode's FramePreview. Omit for the plain centred render. */
  cropRect?: CropRect | null;
  rotation?: Rotation;
  bw?: boolean;
  bwStyle?: BwStyle | null;
  bwStrength?: number | null;
  fit?: boolean;
}) {
  const [mode, setMode] = useState<Mode>('threed');

  /* Warm the room shot the moment a framed preview appears, not when the tab
     is clicked (owner 2026-07-27: "In room" took a visible beat to appear).
     The asset is already lean for what it is — 1600×1240 in 123 KB — so the
     delay was the request starting late, not the bytes. One decode, cached
     for every later tab switch and every other framed preview on the page. */
  useEffect(() => {
    const warm = new Image();
    warm.decoding = 'async';
    warm.src = roomImg;
  }, []);

  // Oriented frame footprint for the room composite (same orientation rule as
  // FramePreview: the frame follows the photo's landscape/portrait).
  const { wPct, aspect, zoom } = useMemo(() => {
    const landscape =
      photo && photo.width > 0
        ? photo.width >= photo.height
        : option.printInches[0] >= option.printInches[1];
    // frameInches, not parseInches(frameSize): an A-series name parses to
    // [NaN], so trueFrac was NaN and the frame drew as a dot on the wall.
    const [a, b] = option.frameInches;
    const lo = Math.min(a, b);
    const hi = Math.max(a, b);
    const [fw, fh] = landscape ? [hi, lo] : [lo, hi];
    const widthM = fw * 0.0254;
    const trueFrac = widthM * ROOM.fracPerMetre;
    // Small frames: zoom the scene in around the anchor so the frame reads at
    // ~minWFrac of the stage; capped so the room still reads as a room.
    const z = Math.min(ROOM.maxZoom, Math.max(1, ROOM.minWFrac / trueFrac));
    let wFrac = Math.min(trueFrac * z, ROOM.maxWFrac);
    // TALL-FRAME CLAMP: the frame hangs from the fixed bottomY line, so its
    // height fraction (wFrac · fh/fw · imgAspect) must leave topMargin of
    // headroom. Shrink the width proportionally when it wouldn't.
    const maxHFrac = ROOM.bottomY - ROOM.topMargin;
    const hFrac = wFrac * (fh / fw) * ROOM.imgAspect;
    if (hFrac > maxHFrac) wFrac *= maxHFrac / hFrac;
    return { wPct: wFrac * 100, aspect: `${fw} / ${fh}`, zoom: z };
  }, [photo, option]);

  return (
    <div className="fpvm">
      <div className="fpvm__tabs" role="group" aria-label="Preview style">
        {MODES.map(([m, label]) => (
          <button
            key={m}
            type="button"
            className={`fpvm__tab${mode === m ? ' fpvm__tab--on' : ''}`}
            aria-pressed={mode === m}
            onClick={() => setMode(m)}
          >
            {label}
          </button>
        ))}
      </div>

      <div className="fpvm__view">
        {mode === 'flat' && (
          <FramePreview photo={photo} option={option} cropRect={cropRect} rotation={rotation} bw={bw} bwStyle={bwStyle} bwStrength={bwStrength} fit={fit} />
        )}

        {mode === 'threed' && (
          <Frame3DView photo={photo} option={option} cropRect={cropRect} rotation={rotation} bw={bw} bwStyle={bwStyle} bwStrength={bwStrength} fit={fit} />
        )}

        {mode === 'room' && (
          <div className="fpvroom">
            <img
              className="fpvroom__bg"
              src={roomImg}
              alt=""
              draggable={false}
              style={
                zoom > 1
                  ? {
                      transform: `scale(${zoom})`,
                      transformOrigin: `${ROOM.anchorX * 100}% ${ROOM.bottomY * 100}%`,
                    }
                  : undefined
              }
            />
            <div
              className="fpvroom__anchor"
              style={{
                left: `${ROOM.anchorX * 100}%`,
                top: `${ROOM.bottomY * 100}%`,
                width: `${wPct}%`,
                aspectRatio: aspect,
              }}
            >
              <FramePreview photo={photo} option={option} cropRect={cropRect} rotation={rotation} bw={bw} bwStyle={bwStyle} bwStrength={bwStrength} fit={fit} />
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
